import {
  Component,
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import type { ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/nextjs";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from "@tanstack/react-table";
import { useTransactions } from "../hooks/useTransactions";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { useWriteQueue } from "../hooks/useWriteQueue";
import { buildColumnDefs } from "../utils/buildColumnDefs";
import { columnWidthPercents } from "../utils/columnSizing";
import { FilterBar } from "./FilterBar";
import { RecordDrawer } from "./RecordDrawer";
import {
  DataTablePaginationSkeleton,
  DataTableSkeleton,
} from "./DataTableSkeleton";
import { cn } from "../utils/cn";
import { buildDataTableThemeVars } from "../utils/theme";
import { IconOverridesContext } from "../utils/iconRegistry";
import { getDisplayText, getDisplayValue } from "../utils/display";
import { formatAmount, formatThaiDateCard } from "../utils/formatters";
import { stripStatusEmoji } from "../utils/statusVisuals";
import {
  getLedgerNameLabel,
  nameMatchesToken,
} from "../utils/similarNameChips";
import { useWideDataLayout } from "../hooks/useWideDataLayout";
import type {
  AeraDataTableProps,
  TableFilters,
  Transaction,
  TransactionSummary,
} from "../types";
import {
  AlertCircle,
  RefreshCw,
  ArrowUpDown,
  Check,
  Minus,
  GitCompareArrows,
  X,
  ChevronRight,
} from "lucide-react";
import { AeraButton, ModalCloseButton, ModalShell } from "@aera/ui";

/** Stable row id for selection / React keys (record_id may arrive wrapped). */
function transactionRowId(
  record: { record_id?: unknown } | null | undefined,
): string {
  return getDisplayText(record?.record_id).trim();
}

function formatCardAmount(value: unknown): string {
  const raw = getDisplayValue(value);
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return "—";
  const rounded = Math.round(n);
  if (Math.abs(n - rounded) < 0.005) {
    return `฿${rounded.toLocaleString("th-TH")}`;
  }
  return formatAmount(n);
}

function DataRecordCard({
  record,
  schema,
  selected,
  onClick,
}: {
  record: Transaction;
  schema: AeraDataTableProps["schema"];
  selected: boolean;
  onClick: () => void;
}) {
  const title =
    getLedgerNameLabel(record, schema.workerId) ||
    getDisplayText(
      record[
        schema.columns.find((col) => col.type === "text")?.id ?? ""
      ],
    ) ||
    "—";
  const statusCol = schema.columns.find((col) => col.type === "status");
  const dateCol = schema.columns.find(
    (col) => col.id === "record_date" || col.type === "datetime" || col.type === "date",
  );
  const amountCol = schema.columns.find((col) => col.type === "amount");
  const statusLabel = statusCol
    ? stripStatusEmoji(getDisplayText(record[statusCol.id]))
    : "";
  const dateLabel = dateCol
    ? formatThaiDateCard(getDisplayText(record[dateCol.id]) || (record[dateCol.id] as string | number))
    : "";
  const amountLabel = amountCol ? formatCardAmount(record[amountCol.id]) : "";
  const meta = [statusLabel, dateLabel].filter(Boolean).join(" • ");

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 border-b border-aera-lightgray px-3 py-3 text-left transition-colors last:border-b-0",
        selected ? "bg-aera-lightgray/70" : "bg-aera-white hover:bg-aera-lightgray/50",
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-aera-charcoal">{title}</p>
        {meta ? (
          <p className="mt-0.5 truncate text-xs text-aera-gray">{meta}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <span className="text-sm font-semibold tabular-nums text-aera-charcoal">
          {amountLabel}
        </span>
        <ChevronRight className="h-4 w-4 text-aera-lightslate" aria-hidden />
      </div>
    </button>
  );
}

function useMinWidthMd() {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setMatches(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return matches;
}

function RecordDrawerErrorModal({ onClose }: { onClose: () => void }) {
  const isDesktop = useMinWidthMd();

  return (
    <ModalShell
      isOpen
      onClose={onClose}
      align={isDesktop ? "center" : "bottom"}
      zIndexClass="z-[10060]"
      panelClassName={cn(
        "flex flex-col overflow-hidden border border-aera-lightgray/80 bg-aera-white text-aera-charcoal",
        isDesktop
          ? "w-full max-w-md rounded-4xl"
          : "w-full max-w-lg rounded-t-2xl rounded-b-none border-t",
      )}
    >
      {!isDesktop ? (
        <div className="flex shrink-0 justify-center pb-1 pt-2">
          <div className="h-1 w-10 rounded-full bg-aera-lightgray" aria-hidden />
        </div>
      ) : null}

      <header className="flex shrink-0 items-center justify-between gap-3 px-4 pb-4 pt-2 sm:px-6">
        <h2 className="min-w-0 text-xl font-bold text-aera-charcoal">
          เปิดรายละเอียดไม่สำเร็จ
        </h2>
        <ModalCloseButton onClose={onClose} className="-mr-1 shrink-0" />
      </header>

      <div className="px-4 pb-4 sm:px-6">
        <div className="flex items-start gap-3">
          <AlertCircle
            className="mt-0.5 h-5 w-5 shrink-0 text-aera-red"
            aria-hidden
          />
          <p className="text-sm leading-relaxed text-aera-gray">
            ระบบบันทึก error ไว้ใน Sentry แล้ว
          </p>
        </div>
      </div>

      <div className="shrink-0 px-4 pt-1 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6">
        <AeraButton
          type="button"
          variant="primary"
          fontWeight="medium"
          fullWidth
          onClick={onClose}
        >
          ปิด
        </AeraButton>
      </div>
    </ModalShell>
  );
}

class AeraDataTableDrawerErrorBoundary extends Component<
  {
    children: ReactNode;
    recordId?: unknown;
    onClose: () => void;
  },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      tags: {
        component: "AeraDataTable",
        boundary: "RecordDrawer",
      },
      extra: {
        recordId: getDisplayText(this.props.recordId),
        componentStack: errorInfo.componentStack,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return <RecordDrawerErrorModal onClose={this.props.onClose} />;
    }

    return this.props.children;
  }
}

function buildDefaultFilters(
  schema: AeraDataTableProps["schema"],
  initialFilters?: Partial<TableFilters>,
): TableFilters {
  const merged: TableFilters = {
    status: null,
    from: null,
    to: null,
    q: null,
    category: null,
    nameToken: null,
    sort: schema.defaultSort.col,
    dir: schema.defaultSort.dir,
    ...initialFilters,
  };
  // Legacy date sorts → created_at (เวลาสร้าง).
  const isBillinLedger =
    schema.workerId === "bill-lin-income" ||
    schema.workerId === "bill-lin-expense";
  if (isBillinLedger && (merged.sort === "recorded_at" || merged.sort === "record_date")) {
    merged.sort = "created_at";
  }
  return merged;
}

export function AeraDataTable({
  schema,
  workspaceId,
  workspaceConfig,
  className,
  onRowClick,
  customRenderers,
  queryOptions,
  apiFetch,
  initialFilters,
  openDrawerOnLoad,
  onSummaryChange,
  onFilterChange,
  onRecordSelect,
  onDrawerAction,
  detailsAddon,
  themeOverrides,
  iconOverrides,
  containerWidth,
  externalSearchQuery,
  onExternalSearchQueryChange,
  onPendingChange,
  onLoadFailure,
}: AeraDataTableProps) {
  const isWideLayout = useWideDataLayout();
  const themeVars = useMemo(
    () => buildDataTableThemeVars(themeOverrides),
    [themeOverrides],
  );
  const [filters, setFilters] = useState<TableFilters>(() =>
    buildDefaultFilters(schema, {
      ...initialFilters,
      ...(externalSearchQuery != null
        ? { q: externalSearchQuery.trim() || null }
        : null),
    }),
  );

  const [selectedRecord, setSelectedRecord] = useState<Transaction | null>(
    null,
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [focusedRowIndex, setFocusedRowIndex] = useState(0);
  const PAGE_SIZE = 50;
  const [pageIndex, setPageIndex] = useState(0);
  const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerInitialTab, setDrawerInitialTab] = useState<
    "details" | "allocation" | "history" | null
  >(null);
  const [statusNow, setStatusNow] = useState(() => Date.now());
  const [showConflictSheet, setShowConflictSheet] = useState(false);
  const isOnline = useOnlineStatus();
  const hasAutoOpenedDrawerRef = useRef(false);
  const dismissedOpenIdRef = useRef<string | null>(null);
  const isDrawerOpenRef = useRef(false);
  isDrawerOpenRef.current = isDrawerOpen;
  const fetchingOpenIdRef = useRef<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const {
    patchWithQueue,
    pendingCount,
    lastSyncedAt,
    conflictCount,
    conflicts,
    isFlushing,
    isResolvingConflicts,
    resolveConflicts,
  } = useWriteQueue(workspaceId, schema.workerId, apiFetch);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isPending,
    status,
    refetch,
    dataUpdatedAt,
  } = useTransactions(schema, workspaceId, filters, {
    apiFetch,
    ...queryOptions,
  });

  const prevPagesLenRef = useRef(0);
  const prevUniqueCountRef = useRef(0);
  const duplicateOnlyPageStreakRef = useRef(0);
  const [fetchStalledOnDuplicates, setFetchStalledOnDuplicates] =
    useState(false);

  useEffect(() => {
    prevPagesLenRef.current = 0;
    prevUniqueCountRef.current = 0;
    duplicateOnlyPageStreakRef.current = 0;
    setFetchStalledOnDuplicates(false);
  }, [schema.workerId, workspaceId, filters]);

  const sourceRows = useMemo(() => {
    const flat = data?.pages.flatMap((page: any) => page.data) ?? [];
    // Dedupe by record_id so rematch ghosts / cursor overlap cannot paint
    // identical payment rows dozens of times (selection then looks "all checked"
    // while selectedCount stays 1 when ids collide or are empty).
    const seen = new Set<string>();
    const out: Transaction[] = [];
    for (const row of flat as Transaction[]) {
      const id = transactionRowId(row);
      if (id) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      out.push(row);
    }
    return out;
  }, [data]);
  const similarNameLabels = useMemo(
    () => sourceRows.map((row) => getLedgerNameLabel(row, schema.workerId)),
    [schema.workerId, sourceRows],
  );
  const allRows = useMemo(() => {
    const token = filters.nameToken?.trim() ?? "";
    if (!token) return sourceRows;
    return sourceRows.filter((row) =>
      nameMatchesToken(getLedgerNameLabel(row, schema.workerId), token),
    );
  }, [filters.nameToken, schema.workerId, sourceRows]);
  const firstPageMeta = data?.pages[0]?.meta;
  // After pages finish (or stall on duplicates), trust unique row count.
  // While still fetching, prefer the larger of unique vs API total for pager UI.
  const totalCount =
    Boolean(filters.nameToken?.trim()) ||
    fetchStalledOnDuplicates ||
    (!hasNextPage && !isFetchingNextPage)
      ? allRows.length
      : Math.max(allRows.length, firstPageMeta?.totalCount ?? 0);
  const pageStart = pageIndex * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const pageRows = useMemo(
    () => allRows.slice(pageStart, pageEnd),
    [allRows, pageStart, pageEnd],
  );
  const amountColId = schema.columns.find((c) => c.type === "amount")?.id;
  const statusColId = schema.columns.find((c) => c.type === "status")?.id;
  const canCancelRecords =
    schema.drawerActions?.some((action) => action.id === "cancel-record") ??
    false;

  const toggleRowSelection = useCallback((recordId: unknown) => {
    const id = getDisplayText(recordId).trim();
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const allVisibleSelected =
    pageRows.length > 0 &&
    pageRows.every((row) => {
      const id = transactionRowId(row);
      return id !== "" && selectedIds.has(id);
    });
  const someVisibleSelected = pageRows.some((row) => {
    const id = transactionRowId(row);
    return id !== "" && selectedIds.has(id);
  });

  const columns = useMemo(() => {
    const selectionColumn: ColumnDef<Transaction, any> = {
      id: "__select__",
      header: () => (
        <AeraButton
          variant="ghost"
          size="xs"
          type="button"
          aria-label={
            allVisibleSelected ? "ล้างการเลือกทั้งหมด" : "เลือกทั้งหมด"
          }
          onClick={(event) => {
            event.stopPropagation();
            setSelectedIds(() => {
              if (allVisibleSelected) return new Set();
              return new Set(
                pageRows
                  .map((row) => transactionRowId(row))
                  .filter(Boolean),
              );
            });
          }}
          className="inline-flex h-5 w-5 min-w-5 max-w-5 p-0! items-center justify-center rounded-md text-aera-lightslate transition-colors hover:bg-aera-lightgray/50 hover:text-aera-charcoal cursor-pointer"
        >
          {allVisibleSelected ? (
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-[5px] bg-(--aera-dt-accent) text-(--aera-dt-accent-text)">
              <Check className="h-3 w-3" />
            </span>
          ) : someVisibleSelected ? (
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-[5px] bg-(--aera-dt-row-selected-surface) text-(--aera-dt-accent-hover)">
              <Minus className="h-3 w-3" />
            </span>
          ) : (
            <span className="block h-3.5 w-3.5 rounded-[4px] border border-(--aera-dt-border-strong)" />
          )}
        </AeraButton>
      ),
      size: 44,
      meta: {
        align: "left",
        className: "",
      },
      cell: ({ row }) => {
        const id = transactionRowId(row.original);
        const checked = id !== "" && selectedIds.has(id);
        return (
          <AeraButton
            variant="ghost"
            size="xs"
            type="button"
            aria-label={checked ? "ยกเลิกการเลือกรายการ" : "เลือกรายการ"}
            onClick={(event) => {
              event.stopPropagation();
              toggleRowSelection(id);
            }}
            className="inline-flex h-5 w-5 min-w-5 max-w-5 p-0! items-center justify-center rounded-md text-aera-lightslate transition-colors hover:bg-aera-lightgray/50 hover:text-aera-charcoal cursor-pointer"
          >
            {checked ? (
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-[5px] bg-(--aera-dt-accent) text-(--aera-dt-accent-text)">
                <Check className="h-3 w-3" />
              </span>
            ) : (
              <span className="block h-3.5 w-3.5 rounded-[4px] border border-(--aera-dt-border-strong)" />
            )}
          </AeraButton>
        );
      },
    };

    return [
      ...(canCancelRecords ? [selectionColumn] : []),
      ...buildColumnDefs(
        schema.columns,
        workspaceConfig,
        customRenderers,
        filters.q,
        themeOverrides,
        workspaceId,
      ),
    ];
  }, [
    canCancelRecords,
    pageRows,
    allVisibleSelected,
    customRenderers,
    filters.q,
    schema.columns,
    selectedIds,
    workspaceConfig,
    workspaceId,
    someVisibleSelected,
    themeOverrides,
    toggleRowSelection,
  ]);

  const table = useReactTable({
    data: pageRows,
    columns,
    defaultColumn: {
      size: 200,
    },
    getRowId: (row, index) => transactionRowId(row) || `row-${index}`,
    getCoreRowModel: getCoreRowModel(),
  });

  const tableColumnSizes = useMemo(
    () => columns.map((column) => column.size ?? 200),
    [columns],
  );

  const tableMinWidth = useMemo(() => {
    const total = tableColumnSizes.reduce((sum, size) => sum + size, 0);
    return Math.max(total, 720);
  }, [tableColumnSizes]);

  const tableColPercents = useMemo(
    () => columnWidthPercents(tableColumnSizes),
    [tableColumnSizes],
  );

  const tableLeafColumnIds = useMemo(
    () => columns.map((column) => column.id ?? ""),
    [columns],
  );

  const handleDrawerOpenChange = useCallback((open: boolean) => {
    if (!open && isDrawerOpenRef.current) {
      hasAutoOpenedDrawerRef.current = true;
      if (openDrawerOnLoad) {
        dismissedOpenIdRef.current = openDrawerOnLoad;
      }
    }
    setIsDrawerOpen(open);
  }, [openDrawerOnLoad]);

  const handleDrawerExited = useCallback(() => {
    setSelectedRecord(null);
  }, []);

  const handleRowClick = useCallback(
    (record: Transaction) => {
      const rowIndex = allRows.findIndex(
        (row) => row.record_id === record.record_id,
      );
      if (rowIndex >= 0) setFocusedRowIndex(rowIndex);
      setSelectedRecord(record);
      setIsDrawerOpen(true);
      onRowClick?.(record);
    },
    [allRows, onRowClick],
  );

  const handleSort = useCallback((colId: string) => {
    setFilters((prev) => {
      const isSame = prev.sort === colId;
      const newDir = isSame && prev.dir === "desc" ? "asc" : "desc";
      return { ...prev, sort: colId, dir: newDir };
    });
  }, []);

  // Adjacent-record navigation
  const currentRowIndex = useMemo(
    () => allRows.findIndex((r) => r.record_id === selectedRecord?.record_id),
    [allRows, selectedRecord],
  );
  const hasPrev = currentRowIndex > 0;
  const hasNext = currentRowIndex < allRows.length - 1;

  const handlePrevRecord = useCallback(() => {
    if (hasPrev) setSelectedRecord(allRows[currentRowIndex - 1]);
  }, [hasPrev, allRows, currentRowIndex]);

  const handleNextRecord = useCallback(() => {
    if (hasNext) setSelectedRecord(allRows[currentRowIndex + 1]);
  }, [hasNext, allRows, currentRowIndex]);

  useEffect(() => {
    setFilters(buildDefaultFilters(schema, initialFilters));
    setSelectedRecord(null);
    setSelectedIds(new Set());
    setFocusedRowIndex(0);
    setPageIndex(0);
    setIsDrawerOpen(false);
    hasAutoOpenedDrawerRef.current = false;
  }, [
    schema.workerId,
    schema.defaultSort.col,
    schema.defaultSort.dir,
    initialFilters,
    schema,
  ]);

  useEffect(() => {
    const pagesLen = data?.pages?.length ?? 0;
    if (pagesLen > prevPagesLenRef.current) {
      const grewUnique = sourceRows.length > prevUniqueCountRef.current;
      if (grewUnique) {
        prevUniqueCountRef.current = sourceRows.length;
        duplicateOnlyPageStreakRef.current = 0;
        setFetchStalledOnDuplicates(false);
      } else if (prevPagesLenRef.current > 0) {
        duplicateOnlyPageStreakRef.current += 1;
        if (duplicateOnlyPageStreakRef.current >= 2) {
          setFetchStalledOnDuplicates(true);
        }
      } else {
        prevUniqueCountRef.current = sourceRows.length;
      }
      prevPagesLenRef.current = pagesLen;
    } else if (sourceRows.length > prevUniqueCountRef.current) {
      prevUniqueCountRef.current = sourceRows.length;
      duplicateOnlyPageStreakRef.current = 0;
      setFetchStalledOnDuplicates(false);
    }
  }, [sourceRows.length, data?.pages?.length]);

  useEffect(() => {
    const needsMoreRows = pageEnd > allRows.length;
    if (!needsMoreRows || !hasNextPage || isFetchingNextPage) return;
    if (fetchStalledOnDuplicates) return;
    void fetchNextPage();
  }, [
    hasNextPage,
    isFetchingNextPage,
    allRows.length,
    fetchNextPage,
    pageEnd,
    pageIndex,
    fetchStalledOnDuplicates,
  ]);

  const setFilter = useCallback((key: string, value: string | null) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (key === "q") onExternalSearchQueryChange?.(value ?? "");
  }, [onExternalSearchQueryChange]);

  useEffect(() => {
    if (externalSearchQuery === undefined) return;
    const next = externalSearchQuery.trim() || null;
    setFilters((prev) => (prev.q === next ? prev : { ...prev, q: next }));
  }, [externalSearchQuery]);

  useEffect(() => {
    onFilterChange?.(filters);
  }, [filters, onFilterChange]);

  useEffect(() => {
    setPageIndex(0);
  }, [filters.q, filters.sort, filters.dir, filters.nameToken]);

  useEffect(() => {
    const summary: TransactionSummary = {
      count: totalCount,
      totalAmount: amountColId
        ? allRows.reduce(
            (sum, row) => sum + (Number(row[amountColId] ?? 0) || 0),
            0,
          )
        : 0,
      currency: "THB",
    };
    onSummaryChange?.(summary);
  }, [allRows, amountColId, onSummaryChange, totalCount]);

  useEffect(() => {
    try {
      onRecordSelect?.(isDrawerOpen ? selectedRecord : null);
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: "AeraDataTable",
          callback: "onRecordSelect",
        },
        extra: {
          isDrawerOpen,
          recordId: getDisplayText(selectedRecord?.record_id),
        },
      });
    }
  }, [isDrawerOpen, onRecordSelect, selectedRecord]);

  useEffect(() => {
    // Allow deep-linking via `drawerTab=history|allocation|details`
    if (typeof window === "undefined") return;
    try {
      const tab = new URL(window.location.href).searchParams.get("drawerTab");
      if (tab === "history" || tab === "details" || tab === "allocation") {
        setDrawerInitialTab(tab);
      } else {
        setDrawerInitialTab(null);
      }
    } catch {
      setDrawerInitialTab(null);
    }
  }, [selectedRecord?.record_id, isDrawerOpen]);

  useEffect(() => {
    const visibleIds = new Set(
      allRows.map((row) => transactionRowId(row)).filter(Boolean),
    );
    setSelectedIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
    setFocusedRowIndex((prev) => {
      if (allRows.length === 0) return 0;
      return Math.min(prev, allRows.length - 1);
    });
  }, [allRows]);

  // Keep the open drawer on the freshest row from the query cache
  // (optimistic patch + refetch), not the stale click snapshot.
  useEffect(() => {
    if (!isDrawerOpen || !selectedRecord) return;
    const selectedId = getDisplayText(selectedRecord.record_id);
    if (!selectedId) return;
    const fresh = allRows.find(
      (row) => getDisplayText(row.record_id) === selectedId,
    );
    if (!fresh || fresh === selectedRecord) return;
    setSelectedRecord(fresh);
  }, [allRows, isDrawerOpen, selectedRecord]);

  useEffect(() => {
    if (!openDrawerOnLoad) return;
    if (dismissedOpenIdRef.current === openDrawerOnLoad) return;
    hasAutoOpenedDrawerRef.current = false;
  }, [openDrawerOnLoad]);

  useEffect(() => {
    if (!openDrawerOnLoad || hasAutoOpenedDrawerRef.current) return;
    if (dismissedOpenIdRef.current === openDrawerOnLoad) return;

    const matchRow = (row: Transaction) => {
      const recordId = getDisplayText(row.record_id);
      const internalId = getDisplayText(
        (row as unknown as { id?: unknown }).id,
      );
      return recordId === openDrawerOnLoad || internalId === openDrawerOnLoad;
    };

    const match = allRows.find(matchRow);
    if (match) {
      hasAutoOpenedDrawerRef.current = true;
      setSelectedRecord(match);
      setIsDrawerOpen(true);
      return;
    }

    const fetcher = apiFetch ?? fetch;
    if (!workspaceId.trim()) return;
    if (fetchingOpenIdRef.current === openDrawerOnLoad) return;
    fetchingOpenIdRef.current = openDrawerOnLoad;
    let cancelled = false;
    const url = `/api/v1/transactions/${encodeURIComponent(openDrawerOnLoad)}?workspace_id=${encodeURIComponent(workspaceId)}`;
    void fetcher(url)
      .then(async (res) => {
        if (!res.ok) return null;
        return res.json() as Promise<unknown>;
      })
      .then((payload) => {
        if (cancelled || payload == null || typeof payload !== "object") return;
        const root = payload as Record<string, unknown>;
        const inner =
          (root.data as Record<string, unknown> | undefined) ??
          (root.transaction as Record<string, unknown> | undefined) ??
          (root.record as Record<string, unknown> | undefined) ??
          root;
        const record = inner as Transaction;
        const recordId = getDisplayText(record.record_id);
        const internalId = getDisplayText(
          (inner as { id?: unknown }).id,
        );
        if (!recordId && !internalId) return;
        if (
          recordId !== openDrawerOnLoad &&
          internalId !== openDrawerOnLoad
        ) {
          return;
        }
        if (hasAutoOpenedDrawerRef.current) return;
        if (dismissedOpenIdRef.current === openDrawerOnLoad) return;
        hasAutoOpenedDrawerRef.current = true;
        setSelectedRecord(
          recordId
            ? record
            : { ...record, record_id: internalId || openDrawerOnLoad },
        );
        setIsDrawerOpen(true);
      })
      .catch(() => {
        /* list match may still succeed on a later page */
      });

    return () => {
      cancelled = true;
    };
  }, [allRows, apiFetch, openDrawerOnLoad, workspaceId]);

  useEffect(() => {
    const timer = window.setInterval(() => setStatusNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const refresh = () => {
      if (document.hidden) return;
      void refetch();
    };

    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);

    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [refetch]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTypingTarget =
        !!target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);

      if (
        (event.key === "/" ||
          ((event.metaKey || event.ctrlKey) &&
            event.key.toLowerCase() === "k")) &&
        !isTypingTarget
      ) {
        event.preventDefault();
        setIsKeyboardNavigating(false);
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
        return;
      }

      if (isTypingTarget || isDrawerOpen || allRows.length === 0) return;

      if (event.key === "j" || event.key === "ArrowDown") {
        event.preventDefault();
        setIsKeyboardNavigating(true);
        setFocusedRowIndex((prev) => {
          const next = Math.min(prev + 1, allRows.length - 1);
          setPageIndex(Math.floor(next / PAGE_SIZE));
          return next;
        });
      }

      if (event.key === "k" || event.key === "ArrowUp") {
        event.preventDefault();
        setIsKeyboardNavigating(true);
        setFocusedRowIndex((prev) => {
          const next = Math.max(prev - 1, 0);
          setPageIndex(Math.floor(next / PAGE_SIZE));
          return next;
        });
      }

      if (event.key === "Enter") {
        event.preventDefault();
        setIsKeyboardNavigating(true);
        const record = allRows[focusedRowIndex];
        if (record) handleRowClick(record);
      }

      if (event.key.toLowerCase() === "x") {
        event.preventDefault();
        setIsKeyboardNavigating(true);
        const record = allRows[focusedRowIndex];
        if (record) toggleRowSelection(record.record_id);
      }

      if (event.key.toLowerCase() === "r") {
        event.preventDefault();
        void refetch();
      }

      if (event.key === "Escape" && selectedIds.size > 0) {
        event.preventDefault();
        setIsKeyboardNavigating(false);
        setSelectedIds(new Set());
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [
    allRows,
    focusedRowIndex,
    handleRowClick,
    isDrawerOpen,
    refetch,
    selectedIds.size,
    toggleRowSelection,
    PAGE_SIZE,
  ]);

  const selectedCount = selectedIds.size;
  const selectedRecords = useMemo(
    () => allRows.filter((row) => selectedIds.has(transactionRowId(row))),
    [allRows, selectedIds],
  );

  const isRowSelected = useCallback(
    (recordId: unknown) => {
      const id = getDisplayText(recordId).trim();
      return id !== "" && selectedIds.has(id);
    },
    [selectedIds],
  );

  const handleBulkCancel = useCallback(async () => {
    if (!statusColId || selectedRecords.length === 0) return;

    await Promise.all(
      selectedRecords.map((record) =>
        patchWithQueue({
          recordId: record.record_id,
          field: statusColId,
          value: "❌ ยกเลิก",
        }),
      ),
    );
    setSelectedIds(new Set());
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("aera:clear-latest-money-undo", {
          detail: { reason: "bulk_record_cancelled" },
        }),
      );
    }
  }, [patchWithQueue, selectedRecords, statusColId]);

  const freshestSyncAt = Math.max(dataUpdatedAt || 0, lastSyncedAt || 0);
  const isStale = freshestSyncAt > 0 && statusNow - freshestSyncAt > 60_000;
  const syncStatusLabel =
    pendingCount > 0
      ? !isOnline
        ? `รอซิงก์ ${pendingCount} รายการ`
        : isFlushing
          ? `กำลังซิงก์ ${pendingCount} รายการ`
          : `มีการแก้ไขรอซิงก์ ${pendingCount} รายการ`
      : isStale
        ? "ข้อมูลอาจไม่ล่าสุด"
        : null;
  const topBarState =
    conflictCount > 0
      ? "conflict"
      : !isOnline
        ? "offline"
        : pendingCount > 0
          ? "syncing"
          : isStale
            ? "stale"
            : null;

  // Hard-fail empty state only after a settled failure (no rows, not in-flight).
  // While pending or retrying from error, keep the skeleton — RQ keeps status
  // "error" until a retry succeeds, which used to flash this empty error too early.
  // Do not key off bare isFetching: empty success + poll would flicker skeleton.
  // Do not skeleton while the record drawer is open — resetQueries used to clear
  // rows and unmount the drawer (modal vanished), then remount after refetch.
  const hasRows = allRows.length > 0;
  const isSettledLoadFailure = status === "error" && !isFetching && !hasRows;
  const isInitialPending =
    !hasRows &&
    !isDrawerOpen &&
    (isPending || (status === "error" && isFetching));

  useEffect(() => {
    onPendingChange?.(isInitialPending);
  }, [isInitialPending, onPendingChange]);

  useEffect(() => {
    onLoadFailure?.(isSettledLoadFailure);
  }, [isSettledLoadFailure, onLoadFailure]);

  if (isInitialPending) {
    return (
      <IconOverridesContext.Provider value={iconOverrides}>
        <div
          className={cn(
            "relative h-full w-full min-w-0 flex min-h-0 flex-col bg-aera-white text-aera-charcoal",
            className,
          )}
          style={themeVars}
        >
          <TopSyncBar
            state={topBarState}
            conflictCount={conflictCount}
            syncStatusLabel={syncStatusLabel}
            freshestSyncAt={freshestSyncAt}
            isOnline={isOnline}
            isResolvingConflicts={isResolvingConflicts}
            onOpenDetails={() => setShowConflictSheet(true)}
            onUseServer={() => void resolveConflicts("use-server")}
            onKeepMine={() => void resolveConflicts("keep-mine")}
          />
          <FilterBar
            schema={schema}
            filters={filters}
            setFilter={setFilter}
            searchInputRef={searchInputRef}
            iconOverrides={iconOverrides}
            containerWidth={containerWidth}
            similarNameLabels={similarNameLabels}
            rangeLabel={
              isWideLayout
                ? pageRows.length === 0
                  ? "แสดง 0 จาก 0"
                  : `แสดง ${pageStart + 1}–${pageStart + pageRows.length} จาก ${totalCount}`
                : undefined
            }
          />
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
            <DataTableSkeleton
              schema={schema}
              workspaceConfig={workspaceConfig}
              className="h-full min-h-0"
            />
          </div>
          <DataTablePaginationSkeleton />
        </div>
      </IconOverridesContext.Provider>
    );
  }

  if (isSettledLoadFailure) {
    return (
      <IconOverridesContext.Provider value={iconOverrides}>
        <div
          className={cn(
            "w-full bg-aera-white min-h-[400px] py-2 flex flex-col items-center justify-center text-center px-6 rounded-b-2xl",
            className,
          )}
          style={themeVars}
        >
          <AlertCircle className="w-12 h-12 text-aera-red mb-4 opacity-20" />
          <h3 className="text-lg font-semibold mb-2 text-aera-red">โหลดข้อมูลไม่สำเร็จ</h3>
          <p className="text-aera-gray mb-6 max-w-xs">
            ตรวจสอบการเชื่อมต่ออินเทอร์เน็ตของคุณแล้วลองใหม่อีกครั้ง
          </p>
          <AeraButton
            type="button"
            variant="primary"
            size="sm"
            loading={isFetching}
            icon={<RefreshCw className="w-4 h-4" />}
            onClick={() => {
              void refetch();
            }}
          >
            ลองใหม่
          </AeraButton>
        </div>
      </IconOverridesContext.Provider>
    );
  }

  return (
    <IconOverridesContext.Provider value={iconOverrides}>
      <div
        className={cn(
          "relative w-full min-w-0 flex min-h-0 flex-col text-aera-charcoal",
          isWideLayout ? "min-h-0 flex-1 overflow-hidden" : "h-auto",
          className,
        )}
        style={themeVars}
      >
        <TopSyncBar
          state={topBarState}
          conflictCount={conflictCount}
          syncStatusLabel={syncStatusLabel}
          freshestSyncAt={freshestSyncAt}
          isOnline={isOnline}
          isResolvingConflicts={isResolvingConflicts}
          onOpenDetails={() => setShowConflictSheet(true)}
          onUseServer={() => void resolveConflicts("use-server")}
          onKeepMine={() => void resolveConflicts("keep-mine")}
        />
        <div className="relative z-10 shrink-0 min-w-0">
          <FilterBar
            schema={schema}
            filters={filters}
            setFilter={setFilter}
            searchInputRef={searchInputRef}
            iconOverrides={iconOverrides}
            containerWidth={containerWidth}
            similarNameLabels={similarNameLabels}
            rangeLabel={
              isWideLayout
                ? pageRows.length === 0
                  ? "แสดง 0 จาก 0"
                  : `แสดง ${pageStart + 1}–${pageStart + pageRows.length} จาก ${totalCount}`
                : undefined
            }
            selectionState={
              canCancelRecords && selectedCount > 0
                ? {
                    count: selectedCount,
                    onClear: () => setSelectedIds(new Set()),
                    onBulkCancel: statusColId
                      ? () => {
                          void handleBulkCancel();
                        }
                      : undefined,
                  }
                : undefined
            }
          />
        </div>

        <div
          className={cn(
            "relative min-h-0 bg-aera-white custom-scrollbar",
            isWideLayout
              ? "flex-1 overflow-x-auto overflow-y-auto"
              : "h-auto overflow-x-auto overflow-y-visible",
          )}
        >
          {allRows.length === 0 && status === "success" ? (
            <div className="flex h-full min-h-0 flex-col items-center justify-center px-3 md:px-4 py-18 text-center animate-in fade-in duration-500">
              <span className="text-5xl mb-3 grayscale opacity-50">
                {schema.emptyState.icon}
              </span>
              <h3 className="text-lg text-aera-charcoal font-bold mb-1">
                {filters.q
                  ? `ไม่พบ "${filters.q}"`
                  : filters.nameToken
                    ? `ไม่พบ "${filters.nameToken}"`
                    : schema.emptyState.title}
              </h3>
              <p className="text-aera-gray mb-5 max-w-full md:max-w-[400px] text-sm leading-relaxed">
                {filters.q || filters.nameToken
                  ? "ลองใช้คำค้นอื่น หรือเคลียร์ตัวกรองเพื่อดูรายการทั้งหมด"
                  : schema.emptyState.body}
              </p>
              {filters.q || filters.nameToken ? (
                <AeraButton
                  variant="primary"
                  fontWeight="medium"
                  onClick={() => {
                    setFilter("q", null);
                    setFilter("nameToken", null);
                  }}
                >
                  ล้างการค้นหา
                </AeraButton>
              ) : (
                schema.emptyState.cta && (
                  <AeraButton
                    as="a"
                    href={schema.emptyState.cta.href}
                    target="_blank"
                    rel="noreferrer"
                    variant="line"
                    fontWeight="medium"
                  >
                    {schema.emptyState.cta.label}
                  </AeraButton>
                )
              )}
            </div>
          ) : isWideLayout ? (
            <table
              className="w-full border-collapse table-fixed"
              style={{ minWidth: `${tableMinWidth}px` }}
            >
              <colgroup>
                {tableColPercents.map((percent, index) => (
                  <col
                    key={tableLeafColumnIds[index] || index}
                    style={{ width: `${percent}%` }}
                  />
                ))}
              </colgroup>
              <thead className="sticky top-0 z-5">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="bg-aera-white/90 backdrop-blur-md border-b border-aera-lightgray"
                  >
                    {headerGroup.headers.map((header) => {
                      const isSortable =
                        (header.column.columnDef.meta as any)?.sortable ===
                        true;
                      const align =
                        (header.column.columnDef.meta as any)?.align === "right"
                          ? "right"
                          : "left";
                      return (
                        <th
                          key={header.id}
                          onClick={
                            isSortable ? () => handleSort(header.id) : undefined
                          }
                          className={cn(
                            "h-10 px-3 text-[11px] font-bold text-aera-lightslate uppercase tracking-wider align-middle",
                            align === "right" ? "text-right" : "text-left",
                            isSortable && "cursor-pointer",
                            (header.column.columnDef.meta as any)?.className,
                          )}
                        >
                          <div
                            className={cn(
                              "flex items-center gap-1.5",
                              align === "right"
                                ? "justify-end"
                                : "justify-start",
                              isSortable &&
                                "group hover:text-aera-charcoal transition-colors",
                            )}
                          >
                            {header.isPlaceholder
                              ? null
                              : (flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                ) as ReactNode)}
                            {isSortable &&
                              (filters.sort === header.id ? (
                                <span className="text-(--aera-dt-accent) font-black drop-shadow-sm">
                                  {filters.dir === "asc" ? "↑" : "↓"}
                                </span>
                              ) : (
                                <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-(--aera-dt-accent)" />
                              ))}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody className="relative">
                {table.getRowModel().rows.map((row, i) => {
                  const absoluteIndex = pageStart + i;
                  const rowSelected = isRowSelected(row.original.record_id);
                  const rowFocused = focusedRowIndex === absoluteIndex;
                  const rowStateClass = rowSelected
                    ? rowFocused && isKeyboardNavigating
                      ? "bg-(--aera-dt-row-selected-hover-surface) shadow-[inset_2px_0_0_var(--aera-dt-accent),inset_0_0_0_1px_var(--aera-dt-border-soft)]"
                      : "bg-(--aera-dt-row-selected-surface) shadow-[inset_2px_0_0_var(--aera-dt-accent)] hover:bg-(--aera-dt-row-selected-hover-surface)"
                    : rowFocused && isKeyboardNavigating
                      ? "bg-(--aera-dt-row-focus-surface) shadow-[inset_2px_0_0_var(--aera-dt-border-strong)]"
                      : "hover:bg-aera-lightgray/50";

                  return (
                    <tr
                      key={row.id}
                      onClick={() => handleRowClick(row.original)}
                      onMouseEnter={() => setIsKeyboardNavigating(false)}
                      aria-selected={rowSelected}
                      className={cn(
                        "h-[36.5px] border-b border-aera-lightgray last:border-b-0 cursor-pointer transition-colors group",
                        rowStateClass,
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={cn(
                            "h-[36.5px] max-h-[36.5px] overflow-hidden px-3 py-0 align-middle text-sm font-medium text-aera-gray transition-colors",
                            rowSelected || (rowFocused && isKeyboardNavigating)
                              ? "text-aera-charcoal"
                              : "group-hover:text-aera-charcoal",
                            (cell.column.columnDef.meta as any)?.className,
                          )}
                        >
                          <div
                            className={cn(
                              "flex h-full max-h-[36.5px] w-full min-w-0 items-center overflow-hidden leading-5",
                              (cell.column.columnDef.meta as any)?.align ===
                                "right"
                                ? "justify-end"
                                : "justify-start",
                            )}
                          >
                            {
                              flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              ) as ReactNode
                            }
                          </div>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="shrink-0 px-3 py-2 text-xs text-aera-gray">
                {pageRows.length === 0
                  ? "แสดง 0 จาก 0"
                  : `แสดง ${pageStart + 1}–${pageStart + pageRows.length} จาก ${totalCount}`}
              </div>
              <div className="min-h-0 flex-1">
                {pageRows.map((record) => {
                  const id = transactionRowId(record);
                  return (
                    <DataRecordCard
                      key={id || JSON.stringify(record)}
                      record={record}
                      schema={schema}
                      selected={isRowSelected(record.record_id)}
                      onClick={() => handleRowClick(record)}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-aera-lightgray bg-aera-white backdrop-blur-md rounded-b-2xl">
          <div
            className={cn(
              "flex items-center justify-between gap-3 px-3 py-3 text-aera-gray md:px-4",
              isWideLayout
                ? "text-[11px] font-bold tracking-[0.08em] uppercase"
                : "text-xs",
            )}
          >
            <div className="flex items-center gap-2">
              {isWideLayout ? (
                <>
                  <span>{`หน้า ${pageIndex + 1}`}</span>
                  <span className="text-aera-gray/70">
                    {totalCount > 0
                      ? `/ ${Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}`
                      : ""}
                  </span>
                  <span className="text-aera-gray/70">{`(แสดง ${PAGE_SIZE}/หน้า)`}</span>
                </>
              ) : (
                <span>
                  {pageRows.length === 0
                    ? "ไม่มีรายการ"
                    : `แสดง ${pageStart + 1}–${pageStart + pageRows.length} จาก ${totalCount}`}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isFetchingNextPage && (
                <div className="inline-flex items-center gap-2 rounded-md bg-aera-white px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-aera-charcoal border border-aera-lightgray">
                  <LoaderIcon /> กำลังโหลด
                </div>
              )}
              <AeraButton
                variant="outline"
                size="xs"
                fontWeight="bold"
                textSize="xs"
                disabled={pageIndex <= 0}
                onClick={() => {
                  setIsKeyboardNavigating(false);
                  setPageIndex((p) => Math.max(p - 1, 0));
                }}
                className="uppercase"
              >
                ก่อนหน้า
              </AeraButton>
              <AeraButton
                variant="outline"
                size="xs"
                fontWeight="bold"
                textSize="xs"
                disabled={
                  !(
                    (pageIndex + 1) * PAGE_SIZE < allRows.length ||
                    (hasNextPage && !fetchStalledOnDuplicates)
                  ) || isFetchingNextPage
                }
                onClick={async () => {
                  setIsKeyboardNavigating(false);
                  const nextPage = pageIndex + 1;
                  const needMore = nextPage * PAGE_SIZE >= allRows.length;
                  if (needMore && hasNextPage && !isFetchingNextPage) {
                    await fetchNextPage();
                  }
                  setPageIndex(nextPage);
                }}
                className="uppercase"
              >
                ถัดไป
              </AeraButton>
            </div>
          </div>
        </div>

        <AeraDataTableDrawerErrorBoundary
          key={getDisplayText(selectedRecord?.record_id) || "record-drawer"}
          recordId={selectedRecord?.record_id}
          onClose={() => handleDrawerOpenChange(false)}
        >
          <RecordDrawer
            open={isDrawerOpen}
            onOpenChange={handleDrawerOpenChange}
            onExited={handleDrawerExited}
            record={selectedRecord}
            schema={schema}
            workspaceId={workspaceId}
            workspaceConfig={workspaceConfig}
            apiFetch={apiFetch}
            themeOverrides={themeOverrides}
            initialTab={drawerInitialTab}
            onTabChange={(tab) => {
              if (typeof window === "undefined") return;
              try {
                const url = new URL(window.location.href);
                if (tab === "history" || tab === "allocation") {
                  url.searchParams.set("drawerTab", tab);
                } else {
                  url.searchParams.delete("drawerTab");
                }
                window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
              } catch {
                // ignore
              }
            }}
            onPrev={hasPrev ? handlePrevRecord : undefined}
            onNext={hasNext ? handleNextRecord : undefined}
            position={{ current: currentRowIndex + 1, total: allRows.length }}
            onDrawerAction={onDrawerAction}
            detailsAddon={detailsAddon}
            onHistoryUndone={() => {
              if (typeof window !== "undefined") {
                window.dispatchEvent(
                  new CustomEvent("aera:income-money-changed", {
                    detail: { reason: "history_undone" },
                  }),
                );
              }
              void refetch();
            }}
          />
        </AeraDataTableDrawerErrorBoundary>
        <ConflictSheet
          open={showConflictSheet && conflicts.length > 0}
          onClose={() => setShowConflictSheet(false)}
          conflicts={conflicts}
          schema={schema}
        />
      </div>
    </IconOverridesContext.Provider>
  );
}

function TopSyncBar({
  state,
  conflictCount,
  syncStatusLabel,
  freshestSyncAt,
  isOnline,
  isResolvingConflicts,
  onOpenDetails,
  onUseServer,
  onKeepMine,
}: {
  state: "conflict" | "offline" | "syncing" | "stale" | null;
  conflictCount: number;
  syncStatusLabel: string | null;
  freshestSyncAt: number;
  isOnline: boolean;
  isResolvingConflicts: boolean;
  onOpenDetails: () => void;
  onUseServer: () => void;
  onKeepMine: () => void;
}) {
  if (!state) return null;

  if (state === "conflict") {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-aera-lightgray bg-aera-lightgray/60 px-4 py-2 text-sm text-(--aera-dt-warning-text) shrink-0">
        <div className="flex items-center gap-2 font-semibold">
          <GitCompareArrows className="h-4 w-4 shrink-0" />
          <span>
            ข้อมูลบางส่วนถูกอัปเดตระหว่างที่คุณออฟไลน์ {conflictCount} รายการ
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AeraButton
            variant="ghost"
            size="xs"
            type="button"
            onClick={onOpenDetails}
            className="rounded-md px-2.5! py-1.5! text-xs font-semibold text-aera-gray hover:bg-aera-lightgray transition-colors cursor-pointer"
          >
            ดูรายการที่อัปเดต
          </AeraButton>
          <AeraButton
            variant="ghost"
            size="xs"
            type="button"
            onClick={onUseServer}
            className="rounded-md px-2.5! py-1.5! text-xs font-semibold text-aera-gray hover:bg-aera-lightgray transition-colors cursor-pointer"
          >
            ใช้ค่าบนเซิร์ฟเวอร์
          </AeraButton>
          <AeraButton
            variant="ghost"
            size="xs"
            type="button"
            disabled={!isOnline || isResolvingConflicts}
            onClick={onKeepMine}
            loading={isResolvingConflicts}
            className="rounded-md bg-(--aera-dt-warning-surface)! px-2.5! py-1.5! text-xs font-semibold text-(--aera-dt-warning-text) hover:bg-aera-lightgray transition-colors cursor-pointer"
          >
            {isResolvingConflicts
              ? "กำลังเขียนทับ..."
              : "เขียนทับด้วยค่าของฉัน"}
          </AeraButton>
        </div>
      </div>
    );
  }

  const label =
    state === "offline"
      ? "ออฟไลน์อยู่ — การแก้ไขจะถูกบันทึกเมื่อเชื่อมต่อใหม่"
      : syncStatusLabel;

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 border-b px-4 py-2 shrink-0",
        state === "offline"
          ? "border-aera-redlight bg-aera-redlight text-xs font-bold text-aera-red"
          : "border-aera-lightgray bg-aera-lightgray/60 text-[11px] font-bold tracking-[0.08em] uppercase text-aera-charcoal",
      )}
    >
      <span>{label}</span>
      {freshestSyncAt > 0 && state !== "offline" && (
        <span className="normal-case tracking-normal font-medium">
          ล่าสุด{" "}
          {new Date(freshestSyncAt).toLocaleTimeString("th-TH", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      )}
    </div>
  );
}

function ConflictSheet({
  open,
  onClose,
  conflicts,
  schema,
}: {
  open: boolean;
  onClose: () => void;
  conflicts: Array<{ recordId: string; field: string; detectedAt?: number }>;
  schema: AeraDataTableProps["schema"];
}) {
  if (!open) return null;

  return (
    <div
      className="absolute inset-0 z-40 flex items-end justify-center bg-(--aera-dt-overlay)/40 md:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-t-[28px] md:rounded-[24px] bg-aera-white shadow-(--aera-dt-shadow-strong)"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-aera-lightgray px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-aera-charcoal">
              รายการที่มีการอัปเดตระหว่างออฟไลน์
            </h3>
            <p className="mt-1 text-sm text-aera-gray">
              ตรวจสอบรายการที่ระบบพบว่ามีการเปลี่ยนแปลงก่อนกลับมาเชื่อมต่อ
            </p>
          </div>
          <AeraButton
            variant="ghost"
            size="xs"
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 p-0! items-center justify-center rounded-md text-aera-lightslate hover:bg-aera-lightgray/50 hover:text-aera-charcoal transition-colors cursor-pointer"
          >
            <X className="h-4 w-4" />
          </AeraButton>
        </div>
        <div className="max-h-[60vh] overflow-auto px-5 py-3">
          <div className="space-y-2">
            {conflicts.map((conflict, index) => {
              const fieldLabel =
                schema.columns.find((column) => column.id === conflict.field)
                  ?.header ?? conflict.field;
              return (
                <div
                  key={`${conflict.recordId}-${conflict.field}-${index}`}
                  className="flex items-start justify-between gap-4 rounded-lg bg-aera-lightgray px-3 py-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-aera-charcoal">
                      {conflict.recordId}
                    </div>
                    <div className="mt-0.5 text-sm text-aera-gray">
                      {fieldLabel}
                    </div>
                  </div>
                  {conflict.detectedAt && (
                    <div className="shrink-0 text-xs font-medium text-aera-lightslate">
                      {new Date(conflict.detectedAt).toLocaleTimeString(
                        "th-TH",
                        { hour: "2-digit", minute: "2-digit" },
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function LoaderIcon() {
  return (
    <svg
      className="animate-spin h-4 w-4 text-aera-lightslate"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
}
